import React, { useEffect, useMemo, useState } from 'react';
import { errorLines, listAllProjects, saveExcludedProjects } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import type { Project } from '../../types';
import { Badge, Button, Card, CardBody, EmptyState, ErrorState, InlineAlert, Skeleton, StatusBadge, TextField, useToast } from '../../ui';
import { useWizard } from './WizardContext';

const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Exclusions step (BRD: "Excluded projects"): campaigns whose respondents are kept out of this target group.
 * Saved with POST .../excluded-projects: the array of ids REPLACES the list and [] clears it. The backend rejects a
 * campaign excluding itself (it is not offered here) and unknown ids. For now it is only stored: it takes effect
 * once respondent selection exists, and the page says so.
 */
export function StepExclusions() {
  const { project, targetGroup, reload, goStep, setDirty } = useWizard();
  const { notify } = useToast();
  const saved = useMemo(() => (targetGroup.excludedProjects || []), [targetGroup.excludedProjects]);
  const savedIds = useMemo(() => saved.map((p) => p.id), [saved]);

  const campaigns = useAsyncData<Project[]>((signal) => listAllProjects({ signal }), []);
  const [selected, setSelected] = useState<string[]>(savedIds);
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim().toLowerCase(), 200);
  const [problems, setProblems] = useState<string[]>([]);
  const [submitting, run] = useSubmitGuard();

  const dirty = !sameIds(selected, savedIds);
  useEffect(() => { setDirty(dirty); return () => setDirty(false); }, [dirty, setDirty]);

  // Everything we may need to show about a campaign, by id: the full list plus the saved summaries (a saved one may
  // no longer be in the list, for example after it was deleted).
  const info = useMemo(() => {
    const map = new Map<string, { id: string; name?: string | null; projectId?: number | string | null; projectStatus?: string | null }>();
    saved.forEach((p) => map.set(p.id, p));
    (campaigns.data || []).forEach((p) => map.set(p.id, p));
    return map;
  }, [saved, campaigns.data]);

  const others = useMemo(() => (campaigns.data || []).filter((p) => p.id !== project.id), [campaigns.data, project.id]);
  const visible = useMemo(
    () => (query ? others.filter((p) => String(p.name || '').toLowerCase().includes(query) || String(p.projectId ?? '').includes(query)) : others),
    [others, query]
  );

  const toggle = (id: string) => { setProblems([]); setSelected((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id])); };

  const save = () => {
    setProblems([]);
    if (!dirty) { goStep('done', { force: true }); return undefined; }
    return run(async () => {
      try {
        await saveExcludedProjects(project.id, targetGroup.id, selected);
        await reload();
        notify(selected.length ? 'Exclusions saved' : 'Exclusions cleared', { tone: 'ok' });
        goStep('done', { force: true });
      } catch (err) {
        setProblems(errorLines(err));
      }
    });
  };

  return (
    <div>
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-ink">Exclusions</h2>
              <p className="mt-1 max-w-2xl text-sm text-muted">
                Respondents from the campaigns you choose here are kept out of this target group, so nobody answers twice. Optional.
              </p>
            </div>
            <Badge tone={selected.length ? 'brand' : 'neutral'}>{selected.length ? `${selected.length} excluded` : 'None excluded'}</Badge>
          </div>
          <div className="mt-3"><InlineAlert tone="info">This is saved with the target group now, and takes effect once respondent selection is available.</InlineAlert></div>

          {selected.length > 0 && (
            <ul aria-label="Excluded campaigns" className="mt-4 flex flex-wrap gap-2">
              {selected.map((id) => {
                const p = info.get(id);
                return (
                  <li key={id} className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 py-1 pl-3 pr-1.5 text-sm font-medium text-brand-800">
                    {p ? (p.name || `Campaign ${p.projectId ?? ''}`) : id}
                    <button type="button" aria-label={`Remove ${p ? p.name : id}`} onClick={() => toggle(id)} className="rounded-full px-1.5 text-brand-700 hover:bg-brand-100">✕</button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-5">
            {campaigns.loading && !campaigns.data ? (
              <div className="space-y-2" aria-busy="true" aria-label="Loading campaigns">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : campaigns.error ? (
              <ErrorState title="Could not load campaigns" message={errorLines(campaigns.error).join(' ')} onRetry={campaigns.reload} />
            ) : others.length === 0 ? (
              <EmptyState title="No other campaigns" description="There are no other campaigns to exclude yet." />
            ) : (
              <>
                <div className="max-w-sm"><TextField label="Search campaigns" type="search" placeholder="Search by name or ID…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
                {visible.length === 0 ? (
                  <p className="mt-3 rounded-lg bg-canvas px-4 py-6 text-center text-sm text-muted">No campaigns match “{search}”.</p>
                ) : (
                  <ul aria-label="Campaigns" className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-line">
                    {visible.map((p) => (
                      <li key={p.id} className="border-b border-line/60 last:border-0 hover:bg-canvas">
                        <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm">
                          <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
                          <span className="min-w-0 flex-1 truncate font-medium text-ink">{p.name || 'Untitled campaign'}</span>
                          <span className="text-xs text-muted">{p.projectId ? `#${p.projectId}` : ''}</span>
                          <StatusBadge status={p.projectStatus} />
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {problems.length > 0 && (
            <div className="mt-4">
              <InlineAlert>{problems.length === 1 ? problems[0] : <><b>{problems[0]}</b><ul className="ml-4 mt-1 list-disc">{problems.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></>}</InlineAlert>
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <Button variant="secondary" onClick={() => goStep('quotas')}>← Back</Button>
            <Button onClick={save} loading={submitting}>{dirty ? 'Save & finish' : 'Finish'}</Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
